import { cssVariables, isDefined } from "@/utils/utils";
import { Card, Carousel, Col, ConfigProvider, Flex, Row, Spin } from "antd";
import { useGetDashboardExtendedStatistics } from "../../../../../common/api/queries";
import { ExtendedDashboardStatistics } from "../../../types";
import { ActiveStateData } from "./components/ActiveStateData";
import { AgentsData } from "./components/AgentsData";
import { ClientsData } from "./components/ClientsData";
import { ContractsData } from "./components/ContractsData";
import { FinancialAcademyData } from "./components/FinancialAcademyData";
import { LevelData } from "./components/LevelData";
import { PersonalAcademyData } from "./components/PersonalAcademyData";
import { RankData } from "./components/RankData";
import { TraineesData } from "./components/TraineesData";

enum EXTENDED_STATISTICS_KEYS {
  TRAINEES,
  PERSONAL_ACADEMY,
  FINANCIAL_ACADEMY,
  ACTIVE_STATE,
  LEVEL,
  RANK,
  CLIENTS,
  CONTRACTS,
  AGENTS,
}

const createResult = (
  data: ExtendedDashboardStatistics,
): { key: EXTENDED_STATISTICS_KEYS; data?: unknown; orderIndex: number }[] => [
  {
    key: EXTENDED_STATISTICS_KEYS.TRAINEES,
    data: isDefined(data.traineesCount)
      ? { traineesCount: data.traineesCount, previousTraineesCount: data.previousTraineesCount }
      : undefined,
    orderIndex: 9,
  },
  {
    key: EXTENDED_STATISTICS_KEYS.FINANCIAL_ACADEMY,
    data:
      isDefined(data?.financialAcademyMissingPoints) || isDefined(data?.financialAcademyMissingEducations)
        ? {
            missingPoints: data?.financialAcademyMissingPoints,
            missingEducations: data?.financialAcademyMissingEducations,
          }
        : undefined,
    orderIndex: 8,
  },
  {
    key: EXTENDED_STATISTICS_KEYS.PERSONAL_ACADEMY,
    data:
      isDefined(data?.personalAcademyMissingTrainees) || isDefined(data?.personalAcademyMissingEducations)
        ? {
            missingTrainees: data?.personalAcademyMissingTrainees,
            missingEducations: data?.personalAcademyMissingEducations,
          }
        : undefined,
    orderIndex: 7,
  },
  {
    key: EXTENDED_STATISTICS_KEYS.ACTIVE_STATE,
    data: isDefined(data.commissionsActiveStatePoints)
      ? { commissionsActiveStatePoints: data.commissionsActiveStatePoints }
      : undefined,
    orderIndex: 6,
  },
  {
    key: EXTENDED_STATISTICS_KEYS.LEVEL,
    data:
      isDefined(data?.currentLevel) && isDefined(data?.nextLevelPoints)
        ? {
            currentLevel: data.currentLevel,
            nextLevelPoints: data.nextLevelPoints,
          }
        : undefined,
    orderIndex: 5,
  },
  {
    key: EXTENDED_STATISTICS_KEYS.RANK,
    data:
      isDefined(data?.currentRank) && isDefined(data?.nextRankCriteriaCount)
        ? {
            currentRank: data?.currentRank,
            nextRankCriteriaCount: data.nextRankCriteriaCount,
          }
        : undefined,
    orderIndex: 4,
  },
  {
    key: EXTENDED_STATISTICS_KEYS.CLIENTS,
    data: isDefined(data.clientsCount)
      ? {
          clientsCount: data.clientsCount,
          premiumClientsCount: data.premiumClientsCount,
          previousClientsCount: data.previousClientsCount,
        }
      : undefined,
    orderIndex: 3,
  },
  {
    key: EXTENDED_STATISTICS_KEYS.CONTRACTS,
    data: isDefined(data.contractsCount)
      ? { contractsCount: data.contractsCount, previousContractsCount: data.previousContractsCount }
      : undefined,
    orderIndex: 2,
  },
  {
    key: EXTENDED_STATISTICS_KEYS.AGENTS,
    data:
      isDefined(data?.brokersCount) || isDefined(data?.tippersCount)
        ? {
            brokersCount: data?.brokersCount,
            tippersCount: data?.tippersCount,
            previousBrokersCount: data?.previousBrokersCount,
            previousTippersCount: data?.previousTippersCount,
          }
        : undefined,
    orderIndex: 1,
  },
];

const getStatisticsItem = (type: EXTENDED_STATISTICS_KEYS, data?: any) => {
  switch (type) {
    case EXTENDED_STATISTICS_KEYS.CLIENTS:
      return <ClientsData data={data} />;
    case EXTENDED_STATISTICS_KEYS.CONTRACTS:
      return <ContractsData data={data} />;
    case EXTENDED_STATISTICS_KEYS.TRAINEES:
      return <TraineesData data={data} />;
    case EXTENDED_STATISTICS_KEYS.AGENTS:
      return <AgentsData data={data} />;
    case EXTENDED_STATISTICS_KEYS.PERSONAL_ACADEMY:
      return <PersonalAcademyData data={data} />;
    case EXTENDED_STATISTICS_KEYS.FINANCIAL_ACADEMY:
      return <FinancialAcademyData data={data} />;
    case EXTENDED_STATISTICS_KEYS.LEVEL:
      return <LevelData data={data} />;
    case EXTENDED_STATISTICS_KEYS.ACTIVE_STATE:
      return <ActiveStateData data={data} />;
    case EXTENDED_STATISTICS_KEYS.RANK:
      return <RankData data={data} />;
    default:
      return undefined;
  }
};

const CARD_HEIGHT = 146;

export const DashboardStatisticsExtendedWidget = () => {
  const { data, isFetching } = useGetDashboardExtendedStatistics();

  const resultData = data
    ? createResult(data)
        .filter((item) => item.data)
        .sort((a, b) => a.orderIndex - b.orderIndex)
    : undefined;

  return (
    <Card
      style={{ height: "100%", borderRadius: 18, userSelect: "none" }}
      styles={{ body: { height: "100%", padding: "20px 24px" } }}
      loading={isFetching}
    >
      <ConfigProvider
        theme={{
          components: { Carousel: { dotOffset: -8 } },
          token: { colorBgContainer: isFetching ? undefined : cssVariables.colorGrey },
        }}
      >
        <Spin spinning={isFetching}>
          {resultData && (
            <Carousel
              style={{ height: "100%", backgroundColor: "white" }}
              autoplay={{ dotDuration: true }}
              autoplaySpeed={5000}
              draggable
              infinite
            >
              <div>
                <Row gutter={24} style={{ height: CARD_HEIGHT }}>
                  {resultData.slice(0, 3).map((item, index) => (
                    <Col
                      key={item.key}
                      span={8}
                      style={{ borderRight: index < 2 ? `1px solid ${cssVariables.colorHover}` : undefined }}
                    >
                      <Flex
                        vertical
                        style={{
                          height: "100%",
                          paddingRight: index !== 2 ? 12 : 0,
                          paddingLeft: index !== 0 ? 12 : 0,
                        }}
                      >
                        {getStatisticsItem(item.key, item.data)}
                      </Flex>
                    </Col>
                  ))}
                </Row>
              </div>

              {resultData.length > 3 ? (
                <div>
                  <Row gutter={24} style={{ height: CARD_HEIGHT }}>
                    {resultData.slice(3, 6).map((item, index) => (
                      <Col
                        key={item.key}
                        span={8}
                        style={{ borderRight: index < 2 ? `1px solid ${cssVariables.colorHover}` : undefined }}
                      >
                        <Flex
                          vertical
                          style={{
                            height: "100%",
                            paddingRight: index !== 2 ? 12 : 0,
                            paddingLeft: index !== 0 ? 12 : 0,
                          }}
                        >
                          {getStatisticsItem(item.key, item.data)}
                        </Flex>
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : undefined}

              {resultData.length > 6 ? (
                <div>
                  <Row gutter={24} style={{ height: CARD_HEIGHT }}>
                    {resultData.slice(6, 9).map((item, index) => (
                      <Col
                        key={item.key}
                        span={8}
                        style={{ borderRight: index < 2 ? `1px solid ${cssVariables.colorHover}` : undefined }}
                      >
                        <Flex
                          vertical
                          style={{
                            height: "100%",
                            paddingRight: index !== 2 ? 12 : 0,
                            paddingLeft: index !== 0 ? 12 : 0,
                          }}
                        >
                          {getStatisticsItem(item.key, item.data)}
                        </Flex>
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : undefined}
            </Carousel>
          )}
        </Spin>
      </ConfigProvider>
    </Card>
  );
};
